// src/features/mcbimItems/selectors/selectMcBimItemsForViewer.ts
import { createSelector } from "@reduxjs/toolkit";
import {
  GetMcbimItemsUniqueClassesApiResponse,
  openapi,
  PostMcbimItemsApiArg,
  PostMcbimItemsApiResponse,
} from "../../openapi";
import { IfcClassesToDisplayMap } from "../DesignTab/designSlice";
import { IfcClassesEnum } from "@multiconsult-group/multiplacer-core";
import { selectProjectNo } from "../globalSelectors";
import { IRootState } from "../app/store";

// frontend/src/McBim/ifcClassMenuSelectors.ts
import { IFC_INSTANCE_CLASS_META } from "@multiconsult-group/multiplacer-core";
import { selectProjectNoAndFloorName } from "./McBimSlice";
import { selectMcBimItemsWithDisciplinesAsArgsWithLoadingInfoForViewer } from "../DesignTab/mcbimItemSelectors";
import {
  inferPrimarySystemKindFromPrimarySystemName,
  IPrimarySystemKind,
  PRIMARY_SYSTEM_SPECS,
} from "../systemsConstants";

const EMPTY_UNIQUE_IFC_CLASSES: GetMcbimItemsUniqueClassesApiResponse = {
  instanceClasses: [],
  typeClasses: [],
  instanceClassCounts: [],
  typeClassCounts: [],
};

export const selectHttpUniqueIfcClasses = (
  state: IRootState
): GetMcbimItemsUniqueClassesApiResponse => {
  const args = selectProjectNoAndFloorName(state);
  if (!args) return EMPTY_UNIQUE_IFC_CLASSES;
  return (
    openapi.endpoints.getMcbimItemsUniqueClasses.select(args)(state).data ??
    EMPTY_UNIQUE_IFC_CLASSES
  );
};

const selectIfcClassesToDisplay = (s: IRootState) => s.design.ifcClassesToDisplay;
const selectActiveIfcClassesSorted = createSelector(
  [selectIfcClassesToDisplay],
  (map: IfcClassesToDisplayMap | undefined): IfcClassesEnum[] => {
    const active = Object.entries(map ?? {})
      .filter(([, isOn]) => isOn === true)
      .map(([k]) => k as IfcClassesEnum);

    active.sort((a, b) => String(a).localeCompare(String(b)));
    return active;
  }
);

const buildArgsForClass = (
  projectNo: number,
  buildingStoryName: string,
  instanceClass: IfcClassesEnum
): PostMcbimItemsApiArg => ({
  body: {
    projectNo,
    buildingStoryName,
    instanceClasses: [instanceClass], // MÅ matche hooken
  },
});

/**
 * Slår ihop alle RTKQ-cachade svar (en per aktiv IFC-klass) till én liste for vieweren.
 * Viktig: Läsning sker från RTK Query state (mcbimApi) via endpoint.select(args).
 */
const selectFloorAndJob = (s: IRootState) => s.design.floorAndJob;
export const selectMcBimItemsWithIfcClassesAsArgsWithLoadingInfoForViewer = createSelector(
  [selectProjectNo, selectFloorAndJob, selectActiveIfcClassesSorted, (s: IRootState) => s],
  (projectNo, floorAndJob, activeClasses, state) => {
    const buildingStoryName = floorAndJob?.floorName;

    if (!projectNo || !buildingStoryName || activeClasses.length === 0) {
      return {
        items: [] as PostMcbimItemsApiResponse[number][],
        isLoading: false,
        isFetching: false,
        errorsByClass: {} as Partial<Record<IfcClassesEnum, unknown>>,
      };
    }

    const items: PostMcbimItemsApiResponse[number][] = [];
    let isLoading = false;
    let isFetching = false;
    const errorsByClass: Partial<Record<IfcClassesEnum, unknown>> = {};

    for (const cls of activeClasses) {
      const args = buildArgsForClass(projectNo, buildingStoryName, cls);

      const q = openapi.endpoints.postMcbimItems.select(args)(state);

      if (q.isLoading) isLoading = true;
      if (q.error) errorsByClass[cls] = q.error;

      const data = q.data as PostMcbimItemsApiResponse | undefined;
      if (data?.length) {
        items.push(...data);
      }
    }

    return {
      items,
      isLoading,
      isFetching,
      errorsByClass,
    };
  }
);

const selectMcbimItemsFilterMode = (s: IRootState) => s.design.mcbimItemsFilterMode;
export const selectMcBimItemsForViewer = createSelector(
  [
    selectMcBimItemsWithIfcClassesAsArgsWithLoadingInfoForViewer,
    selectMcBimItemsWithDisciplinesAsArgsWithLoadingInfoForViewer,
    selectMcbimItemsFilterMode,
  ],
  (infoWithIfcClasses, infoWithDisciplines, filterMode) => {
    if (filterMode === "DISCIPLINE") {
      return infoWithDisciplines.items;
    }
    if (filterMode === "INSTANCE_CLASS") {
      return infoWithIfcClasses.items;
    }
    return [];
  }
);

// ---- Base state ----
const selectIfcClassesToDisplayMap = (s: IRootState) => s.design.ifcClassesToDisplay ?? {};

// ---- Counts: class -> count ----
export const selectInstanceCountsByClass = createSelector(
  [selectHttpUniqueIfcClasses],
  (unique) => {
    const out: Record<string, number> = {};
    for (const row of unique?.instanceClassCounts ?? []) {
      if (row?.ifcClass) out[row.ifcClass] = row.count ?? 0;
    }
    return out;
  }
);

// ---- Lists ----
export const selectKnownIfcClassesToToggle = createSelector(
  [selectInstanceCountsByClass],
  (counts) => {
    return Object.keys(IFC_INSTANCE_CLASS_META)
      .filter((cls) => (counts[cls] ?? 0) > 0)
      .sort((a, b) => a.localeCompare(b));
  }
);

export const selectUnclassifiedIfcClassesToToggle = createSelector(
  [selectInstanceCountsByClass],
  (counts) => {
    return Object.keys(counts)
      .filter((cls) => (counts[cls] ?? 0) > 0)
      .filter((cls) => IFC_INSTANCE_CLASS_META[cls] == null)
      .sort((a, b) => a.localeCompare(b));
  }
);

export const selectAllIfcClassesToToggle = createSelector(
  [selectKnownIfcClassesToToggle, selectUnclassifiedIfcClassesToToggle],
  (known, unclassified) => [...known, ...unclassified]
);

// ---- Button state ----
export const selectIfcClassesMenuAnyActive = createSelector(
  [selectAllIfcClassesToToggle, selectIfcClassesToDisplayMap],
  (allClasses, displayMap) => allClasses.some((cls) => !!displayMap[cls])
);

export const selectIfcClassesMenuAllSelected = createSelector(
  [selectAllIfcClassesToToggle, selectIfcClassesToDisplayMap],
  (allClasses, displayMap) => allClasses.length > 0 && allClasses.every((cls) => !!displayMap[cls])
);

// ---- Per-row ----
export const makeSelectIsIfcClassActive = (cls: string) =>
  createSelector([selectIfcClassesToDisplayMap], (displayMap) => !!displayMap[cls]);

export const makeSelectIfcClassCount = (cls: string) =>
  createSelector([selectInstanceCountsByClass], (counts) => counts[cls] ?? 0);

const selectMcBimItemsColorMode = (s: IRootState) => s.design.mcbimItemColorMode;
export const selectUniquePrimarySystemKindsForLegend = createSelector(
  [selectMcBimItemsForViewer, selectMcBimItemsColorMode],
  (items, colorMode): IPrimarySystemKind[] => {
    if (colorMode !== "PRIMARY_SYSTEM") return [];
    if (!items?.length) return [];

    const set = new Set<IPrimarySystemKind>();

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // platt: system: string | null
      const systemName = (item as any)?.system as string | null | undefined;

      const kind = inferPrimarySystemKindFromPrimarySystemName(systemName);

      // Vill du visa OTHER i legend? om inte, filtrera bort:
      if (kind !== "OTHER") set.add(kind);
    }

    return Array.from(set).sort(
      (a, b) => PRIMARY_SYSTEM_SPECS[a].sortOrder - PRIMARY_SYSTEM_SPECS[b].sortOrder
    );
  }
);

export interface IPrimarySystemLegendItem {
  kind: IPrimarySystemKind;
  label: string;
  color: string;
}

export const selectPrimarySystemLegendItems = createSelector(
  [selectUniquePrimarySystemKindsForLegend],
  (kinds): IPrimarySystemLegendItem[] =>
    kinds.map((kind) => ({
      kind,
      label: PRIMARY_SYSTEM_SPECS[kind].label,
      color: PRIMARY_SYSTEM_SPECS[kind].color,
    }))
);
